import { ContactIntake } from "./intake.js";
import { useState } from "preact/hooks";
import { locale } from "../i18n.js";
import { CHANNEL_LABEL, fmtDateTime, get, html, LOST_REASON_LABEL, navigate, post, relTime, STAGES, stageLabel, tr } from "../lib.js";
import { Badge, Empty, Field, Loading, Modal, PageHeader, toast, useAction, useLoad } from "../ui.js";
import { PageGuide } from "../guide.js";

export function ContactsPage({ t }) {
  const [intakeContact, setIntakeContact] = useState(null);
  const [stage, setStage] = useState(() => new URLSearchParams(location.search).get("stage") ?? "");
  const [q, setQ] = useState("");
  const [owner, setOwner] = useState("");
  const [importing, setImporting] = useState(() => new URLSearchParams(location.search).has("import"));
  const isAdmin = t.role !== "agent";
  const [data, reload] = useLoad(
    () => get(`${t.base}/contacts?${new URLSearchParams({ ...(stage && { stage }), ...(q && { q }), ...(owner && { owner }) })}`),
    [t.id, stage, q, owner],
  );

  const openChat = async (contactId) => {
    const list = await get(`${t.base}/conversations?contact_id=${contactId}`);
    const cv = list.conversations[0];
    if (cv) navigate(`/inbox/${cv.id}`);
    else toast(tr("No chat with this customer yet. It starts when they write to you or receive a message."));
  };

  return html`<div>
    <${PageHeader} title=${tr("Contacts")} subtitle=${tr("Every person who messaged you, with where they are in the funnel")}>
      ${isAdmin && html`<button class="btn" onClick=${() => setImporting(true)}>${tr("Import customers")}</button>`}
    </${PageHeader}>
    ${intakeContact && html`<${Modal} title=${tr("Intake forms")} onClose=${() => setIntakeContact(null)}><${ContactIntake} key=${t.id + ":" + intakeContact} t=${t} contactId=${intakeContact} /></${Modal}>`}
    ${importing && html`<${ImportModal} t=${t} onClose=${() => setImporting(false)} onDone=${reload} />`}
    <${PageGuide} id="contacts" />
    <div class="chips">
      <button class=${!stage ? "chip active" : "chip"} onClick=${() => setStage("")}>${tr("All")}</button>
      ${STAGES.map((s) => html`<button class=${stage === s ? "chip active" : "chip"} onClick=${() => setStage(s)}>${stageLabel(s)} <span class="count">${data?.stages?.[s] ?? 0}</span></button>`)}
    </div>
    <div class="chips">
      ${[["", "Anyone's"], ["me", "Mine"], ["unassigned", "Unassigned"]].map(([k, label]) => html`<button class=${owner === k ? "chip active" : "chip"} onClick=${() => setOwner(k)}>${tr(label)}</button>`)}
    </div>
    <input type="search" class="search" placeholder=${tr("Search name, phone or email")} value=${q} onInput=${(e) => setQ(e.target.value)} />
    ${!data && html`<${Loading} />`}
    ${data && !data.contacts.length && html`<${Empty} title=${tr("No contacts here yet")} />`}
    ${data?.contacts.length > 0 && html`<div class="card table-wrap"><table class="table">
      <thead><tr><th>${tr("Name")}</th><th>${tr("Channel")}</th><th>${tr("Stage")}</th><th>${tr("Interest")}</th><th>${tr("Owner")}</th><th>${tr("Next task")}</th><th>${tr("Last message")}</th></tr></thead>
      <tbody>${data.contacts.map((c) => html`<tr class="clickable" onClick=${() => openChat(c.id)}>
        <td><strong>${c.name || "—"}</strong><div class="muted small">${c.phone ?? c.email ?? ""}</div><button class="link" onClick=${e => { e.stopPropagation(); setIntakeContact(c.id); }}>${tr("Intake forms")}</button></td>
        <td>${CHANNEL_LABEL[c.channel_type]}${c.import_id ? html`<div class="muted small">${tr("imported")}</div>` : ""}${c.source && c.source !== "direct" ? html`<div class="muted small" title=${c.campaign ?? ""}>${tr("via {source}", { source: c.source })}${c.campaign ? ` · ${c.campaign}` : ""}</div>` : ""}</td>
        <td><${Badge} kind=${c.stage === "booked" || c.stage === "customer" ? "ok" : c.stage === "lost" ? "muted" : "neutral"}>${stageLabel(c.stage)}</${Badge}>${c.opted_out ? html` <${Badge} kind="warn">${tr("opted out")}</${Badge}>` : c.marketing_opt_in ? html` <${Badge} kind="ok">${tr("offers ok")}</${Badge}>` : ""}
          ${c.lost_reason && html`<div class="muted small">${tr(LOST_REASON_LABEL[c.lost_reason])}</div>`}</td>
        <td class="small">${c.interest ?? ""}</td>
        <td class="small">${c.owner_name ?? html`<span class="muted">—</span>`}</td>
        <td class="small nowrap ${c.next_task_due && Date.parse(c.next_task_due) < Date.now() ? "error-text" : ""}">${c.next_task_due ? fmtDateTime(c.next_task_due, t.timezone) : ""}</td>
        <td class="muted small">${c.last_inbound_at ? relTime(c.last_inbound_at) : "—"}</td>
      </tr>`)}</tbody>
    </table></div>`}
  </div>`;
}

// ── Import ───────────────────────────────────────────────────────────────────

const FIELDS = [
  ["phone", "Mobile number"],
  ["name", "Full name"],
  ["first_name", "First name"],
  ["last_name", "Last name"],
  ["email", "Email"],
  ["notes", "Notes"],
];

const PROBLEM_LABEL = {
  missing_phone: "no number",
  invalid_phone: "not a phone number",
  not_mobile: "landline, no WhatsApp",
  duplicate: "repeated in the file",
};

/** The file's text. Excel in Hebrew often saves CSV as Windows-1255 rather than UTF-8. */
async function readCsv(file) {
  const bytes = await file.arrayBuffer();
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    return new TextDecoder("windows-1255").decode(bytes);
  }
}

function ImportModal({ t, onClose, onDone }) {
  const [busy, run] = useAction();
  const [file, setFile] = useState(null); // { name, content }
  const [preview, setPreview] = useState(null);
  const [consent, setConsent] = useState("service");
  const [confirmed, setConfirmed] = useState(false);
  const [stage, setStage] = useState("customer");
  const [result, setResult] = useState(null);
  const [history, reloadHistory] = useLoad(() => get(`${t.base}/contacts/imports`), [t.id]);

  const check = (f, mapping) => run(async () => setPreview(await post(`${t.base}/contacts/import/preview`, { file_name: f.name, content: f.content, mapping })));
  const choose = async (e) => {
    const picked = e.target.files?.[0];
    if (!picked) return;
    const f = { name: picked.name, content: await readCsv(picked) };
    setFile(f);
    setResult(null);
    await check(f).catch(() => {});
  };
  const remap = (field, value) => {
    const mapping = { ...preview.mapping };
    if (value === "") delete mapping[field];
    else mapping[field] = Number(value);
    check(file, mapping).catch(() => {});
  };
  const importNow = async () => {
    const r = await run(() => post(`${t.base}/contacts/import`, { file_name: file.name, content: file.content, mapping: preview.mapping, consent, consent_confirmed: confirmed, stage }));
    setResult(r);
    setFile(null);
    setPreview(null);
    reloadHistory();
    onDone();
  };
  const undo = async (id) => {
    if (!window.confirm(tr("Remove the customers this file added? Anyone who has written in or booked since stays."))) return;
    const r = await run(() => post(`${t.base}/contacts/imports/${id}/undo`));
    toast(tr("{removed} removed, {kept} kept because they're active", r));
    reloadHistory();
    onDone();
  };
  const n = (x) => Number(x ?? 0).toLocaleString(locale());

  return html`<${Modal} title=${tr("Import customers")} onClose=${onClose} wide>
    <p class="muted">${tr("Bring your existing customers in from a spreadsheet. Importing sends nothing: the agent recognises them when they write, and reminders reach them like anyone else.")}</p>
    ${result && html`<div class="callout ok small">${tr("Imported: {added} new, {updated} already on file and filled in, {skipped} skipped.", { added: n(result.added), updated: n(result.updated), skipped: n(result.skipped) })}</div>`}
    <${Field} label=${tr("CSV file")} hint=${tr("In Excel or Google Sheets: File → Save as / Download → CSV. One customer per row, with a mobile number. Up to 5,000 rows.")}>
      <input type="file" accept=".csv,text/csv" disabled=${busy} onChange=${choose} />
    </${Field}>

    ${preview && html`
      <h3>${tr("Columns")}</h3>
      <div class="row">${FIELDS.map(([f, label]) => html`<${Field} label=${tr(label)}>
        <select value=${preview.mapping[f] ?? ""} disabled=${busy} onChange=${(e) => remap(f, e.target.value)}>
          <option value="">${tr("Not in the file")}</option>
          ${preview.headers.map((h, i) => html`<option value=${i}>${h || `#${i + 1}`}</option>`)}
        </select></${Field}>`)}</div>
      <p>
        <strong>${n(preview.valid)}</strong> ${tr("customers to import")}
        <span class="muted">(${tr("{new} new, {existing} already in your contacts", { new: n(preview.new), existing: n(preview.existing) })})</span>
        ${preview.rows_total > preview.valid && html` · <span class="muted">${tr("{n} rows skipped", { n: n(preview.rows_total - preview.valid) })}</span>`}
      </p>
      ${preview.sample.length > 0 && html`<div class="table-wrap"><table class="table small">
        <thead><tr><th>${tr("Line")}</th><th>${tr("Name")}</th><th>${tr("Mobile number")}</th><th>${tr("Email")}</th></tr></thead>
        <tbody>${preview.sample.map((r) => html`<tr><td class="muted">${r.line}</td><td dir="auto">${r.name ?? "—"}</td><td dir="ltr">${r.phone}</td><td>${r.email ?? ""}</td></tr>`)}</tbody>
      </table></div>`}
      ${preview.problems.length > 0 && html`<details><summary class="muted small">${tr("Skipped rows")}</summary>
        <ul class="plain small">${preview.problems.map((p) => html`<li>${tr("Line {line}", { line: p.line })}: <span dir="ltr">${p.value || "—"}</span> · ${tr(PROBLEM_LABEL[p.problem] ?? p.problem)}</li>`)}</ul>
      </details>`}

      <h3>${tr("Stage")}</h3>
      <select value=${stage} onChange=${(e) => setStage(e.target.value)}>
        ${STAGES.filter((s) => s !== "lost" && s !== "booked").map((s) => html`<option value=${s}>${stageLabel(s)}</option>`)}
      </select>
      <p class="muted small">${tr("For customers who are new to your contacts. Existing contacts keep their stage.")}</p>

      <h3>${tr("Offers and campaigns")}</h3>
      <label class="toggle choice"><input type="radio" name="consent" checked=${consent === "service"} onChange=${() => setConsent("service")} />
        <span><strong>${tr("Service messages only (recommended)")}</strong><div class="muted small">${tr("Replies, reminders and booking updates. Each customer joins campaigns once they agree to offers in a chat with the agent.")}</div></span></label>
      <label class="toggle choice"><input type="radio" name="consent" checked=${consent === "marketing"} onChange=${() => setConsent("marketing")} />
        <span><strong>${tr("They already agreed to receive offers")}</strong><div class="muted small">${tr("Only if you have documented consent from every customer in the file.")}</div></span></label>
      ${consent === "marketing" && html`<div class="callout warn small">
        <label class="toggle choice"><input type="checkbox" checked=${confirmed} onChange=${(e) => setConfirmed(e.target.checked)} />
          ${tr("I confirm every customer in this file gave documented consent to receive advertising messages from my business (Communications Law, section 30A). The confirmation is recorded with my name.")}</label>
      </div>`}

      <p class="muted small">${preview.allowance.included == null
        ? tr("Imported customers cost nothing until the agent answers them.")
        : tr("Imported customers cost nothing until the agent answers them. Each conversation then counts toward your plan's {included} a month ({used} used so far).", { included: n(preview.allowance.included), used: n(preview.allowance.used) })}</p>
      <div class="row-actions">
        <button class="btn primary" disabled=${busy || !preview.valid || (consent === "marketing" && !confirmed)} onClick=${importNow}>${tr("Import {n} customers", { n: n(preview.valid) })}</button>
      </div>`}

    ${history?.imports?.length > 0 && html`<h3>${tr("Previous imports")}</h3>
      <ul class="plain small">${history.imports.map((i) => html`<li>
        <strong dir="auto">${i.file_name}</strong> · ${fmtDateTime(i.created_at, t.timezone)} · ${tr("{added} new, {updated} filled in", { added: n(i.added), updated: n(i.updated) })}
        ${i.consent === "marketing" ? html` · <${Badge} kind="ok">${tr("offers ok")}</${Badge}>` : ""}
        ${i.undone_at
          ? html` · <span class="muted">${tr("undone, {n} removed", { n: n(i.removed) })}</span>`
          : html` <button class="btn small ghost" disabled=${busy} onClick=${() => undo(i.id)}>${tr("Undo")}</button>`}
      </li>`)}</ul>`}
  </${Modal}>`;
}

